/**
 * Build a guarded page route from ONE access list (#3989 review C3): the element's guard and
 * the route's `handle.anyOf` come from the same `PageAccess`, so they cannot drift apart.
 *
 * Route tables are evaluated while the module registry is still loading (registry -> routes
 * -> components -> ModuleContext -> registry), so guardPage itself only calls
 * React.createElement on a component defined HERE; the guard components are resolved when
 * GuardedPage renders, by which time every module has finished loading.
 */
import React from 'react';
import type { RouteObject } from 'react-router-dom';
import { FINANCE_PAGE, type PageAccess } from './pageAccess';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { ModuleAccessRoute } from '../components/ModuleAccessRoute';
import { FinanceAccessRoute } from '../components/FinanceAccessRoute';

export interface PageGuardHandle {
  readonly anyOf: PageAccess['anyOf'];
  readonly allowExternal: boolean;
}

export interface GuardedPageProps {
  readonly access: PageAccess;
  readonly children: React.ReactNode;
}

/** Signed-in check, then the page's module gate (the finance wording for P4 pages). */
export function GuardedPage({ access, children }: GuardedPageProps) {
  const gate =
    access === FINANCE_PAGE ? (
      <FinanceAccessRoute>{children}</FinanceAccessRoute>
    ) : (
      <ModuleAccessRoute anyOf={access.anyOf} allowExternal={access.allowExternal === true}>
        {children}
      </ModuleAccessRoute>
    );
  return <ProtectedRoute>{gate}</ProtectedRoute>;
}

/** `{ element, handle }` for a route whose page opens only for `access`. */
export function guardPage(
  access: PageAccess,
  page: React.ReactNode,
): Required<Pick<RouteObject, 'element'>> & { handle: PageGuardHandle } {
  return {
    element: React.createElement(GuardedPage, { access, children: page }),
    handle: { anyOf: access.anyOf, allowExternal: access.allowExternal === true },
  };
}
